/**
 * MobX Store для Query Builder
 * Управляет деревом групп и критериев
 */

import { makeAutoObservable } from "mobx";
import { RemoteData } from "src/common/RemoteData";

import type { ZFilterMetadataLoader } from "./api/filterMetadataApi";
import { QueryBuilderDraftStore } from "./QueryBuilderDraftStore";
import type {
  ZFilterGroup,
  ZFilterValue,
  ZFilterValueType,
} from "./types/ZFilterApiTypes";
import type {
  ZFilterableField,
  ZOperatorDefinition,
  ZOperatorsMapping,
} from "./types/ZFilterMetadataTypes";
import { defaultLogicalOperator } from "./types/ZQueryBuilderConstants";
import {
  ZCriterionNode,
  ZGroupNode,
  ZLogicalOperator,
  ZNodePath,
} from "./types/ZQueryBuilderTypes";
import {
  validationContexts,
  ZValidationContext,
} from "./types/ZValidationContext";
import { countCriteria } from "./utils/countCriteria";
import { deserializeFilterTree } from "./utils/deserializer";
import { operatorRequiresValue } from "./utils/filterUtils";
import {
  canMoveNodeByDirection,
  getChildrenMoveCapabilities,
  moveNodeByDirection,
} from "./utils/nodeMove";
import { serializeFilterTree } from "./utils/serializer";
import {
  createCriterion,
  createEmptyGroup,
  findNodeByPath,
  getParentGroup,
  isGroupNode,
} from "./utils/treeUtils";
import { validateTree as validateFilterTree } from "./utils/validation";

const buildInitialGroup = (): ZGroupNode => {
  const group = createEmptyGroup(defaultLogicalOperator);
  group.items = [createCriterion()];
  return group;
};

type QueryBuilderMetadataData = {
  fields: ZFilterableField[];
  operatorsMapping: ZOperatorsMapping;
};

const emptyOperatorsMapping: ZOperatorsMapping = {
  TEXT: [],
  NUMBER: [],
  DATETIME: [],
  BOOLEAN: [],
};

export class QueryBuilderStore {
  rootGroup: ZGroupNode;

  setRootGroup(value: ZGroupNode): void {
    this.rootGroup = value;
  }

  data: RemoteData<QueryBuilderMetadataData> = { status: "none" };

  setData(newData: RemoteData<QueryBuilderMetadataData>): void {
    this.data = newData;
  }

  draftStore: QueryBuilderDraftStore;

  setDraftStore(value: QueryBuilderDraftStore): void {
    this.draftStore = value;
  }

  constructor(private readonly metadataLoader: ZFilterMetadataLoader) {
    this.rootGroup = buildInitialGroup();
    this.draftStore = new QueryBuilderDraftStore({
      getSerializedFilter: () =>
        JSON.stringify(this.toFilterGroup(validationContexts.saveTemplate)),
      validateFilterTree: (context) =>
        validateFilterTree(
          this.rootGroup,
          this.currentOperatorsMapping,
          context,
        ).isValid,
    });
    makeAutoObservable(this);
  }

  private resetMetadataState(): void {
    this.setData({ status: "none" });
  }

  async init(): Promise<void> {
    this.setData({ status: "wait" });
    try {
      const response = await this.metadataLoader();
      this.setData({
        status: "ready",
        result: {
          fields: response.fields,
          operatorsMapping: response.operatorsMapping,
        },
      });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  }

  dispose(): void {
    this.resetMetadataState();
    this.reset();
  }

  reset(): void {
    this.setRootGroup(buildInitialGroup());
    this.draftStore.reset();
  }

  resetConditions(): void {
    this.setRootGroup(buildInitialGroup());
    this.draftStore.revalidateTreeIfNeeded();
  }

  get availableFields(): ZFilterableField[] {
    return this.data.status === "ready" ? this.data.result.fields : [];
  }

  private get currentOperatorsMapping(): ZOperatorsMapping {
    return this.data.status === "ready"
      ? this.data.result.operatorsMapping
      : emptyOperatorsMapping;
  }

  getOperatorsForValueType(
    valueType?: ZFilterValueType | null,
  ): ZOperatorDefinition[] {
    if (!valueType) return [];
    return (
      this.currentOperatorsMapping[valueType as keyof ZOperatorsMapping] || []
    );
  }

  doesOperatorRequireValue(
    operatorCode: string,
    valueType: ZFilterValueType,
  ): boolean {
    return operatorRequiresValue(
      operatorCode,
      this.getOperatorsForValueType(valueType),
    );
  }

  /**
   * Action: добавление новой группы
   * @param parentPath - путь к родительской группе
   * @param operator - логический оператор новой группы
   */
  addGroup(parentPath: ZNodePath, operator: ZLogicalOperator): void {
    const parent = findNodeByPath(this.rootGroup, parentPath);

    if (!parent || !isGroupNode(parent)) {
      return;
    }

    const newGroup = createEmptyGroup(operator);
    // Новая группа создается с одним пустым критерием
    newGroup.items = [createCriterion()];

    parent.items.push(newGroup);
    this.draftStore.revalidateTreeIfNeeded();
  }

  wrapCriterionInGroup(
    criterionPath: ZNodePath,
    operator: ZLogicalOperator,
  ): void {
    const parent = getParentGroup(this.rootGroup, criterionPath);
    if (!parent) {
      return;
    }

    const index = criterionPath[criterionPath.length - 1];
    if (index === undefined) {
      return;
    }

    const criterion = parent.items[index];
    if (!criterion || isGroupNode(criterion)) {
      return;
    }

    const newGroup = createEmptyGroup(operator);
    newGroup.items = [criterion];

    parent.items.splice(index, 1, newGroup);
    this.draftStore.revalidateTreeIfNeeded();
  }

  private removeItemAtPath(itemPath: ZNodePath): void {
    if (itemPath.length === 0) {
      return;
    }

    const parent = getParentGroup(this.rootGroup, itemPath);
    if (!parent) {
      return;
    }

    const index = itemPath[itemPath.length - 1];
    if (index === undefined) {
      return;
    }
    parent.items.splice(index, 1);
  }

  private pruneEmptyGroups(): void {
    const prune = (group: ZGroupNode): void => {
      for (let index = group.items.length - 1; index >= 0; index -= 1) {
        const item = group.items[index];
        if (!item || !isGroupNode(item)) {
          continue;
        }

        prune(item);
        if (item.items.length === 0) {
          group.items.splice(index, 1);
        }
      }
    };

    prune(this.rootGroup);
  }

  /** Action: удаление группы */
  removeGroup(groupPath: ZNodePath): void {
    this.removeItemAtPath(groupPath);
    this.pruneEmptyGroups();
    this.draftStore.revalidateTreeIfNeeded();
  }

  /**
   * Action: установка оператора группы
   * @param groupPath - путь к группе
   * @param operator - логический оператор
   */
  setGroupOperator(groupPath: ZNodePath, operator: ZLogicalOperator): void {
    const group = findNodeByPath(this.rootGroup, groupPath);

    if (!group || !isGroupNode(group)) {
      return;
    }

    group.op = operator;
    this.draftStore.revalidateTreeIfNeeded();
  }

  /**
   * Action: добавление пустого критерия
   * @param parentPath - путь к родительской группе
   */
  addCriterion(parentPath: ZNodePath): void {
    const parent = findNodeByPath(this.rootGroup, parentPath);

    if (!parent || !isGroupNode(parent)) {
      return;
    }

    parent.items.push(createCriterion());
    this.draftStore.revalidateTreeIfNeeded();
  }

  /** Action: удаление критерия */
  removeCriterion(criterionPath: ZNodePath): void {
    this.removeItemAtPath(criterionPath);
    this.pruneEmptyGroups();
    this.draftStore.revalidateTreeIfNeeded();
  }

  /**
   * Action: обновление критерия
   * @param criterionPath - путь к критерию
   * @param updates - частичное обновление полей критерия
   */
  updateCriterion(
    criterionPath: ZNodePath,
    updates: Partial<
      Pick<ZCriterionNode, "fieldMeta" | "operator" | "value" | "valueType">
    >,
  ): void {
    const criterion = findNodeByPath(this.rootGroup, criterionPath);

    if (!criterion || isGroupNode(criterion)) {
      return;
    }

    if (updates.fieldMeta !== undefined)
      criterion.fieldMeta = updates.fieldMeta;
    if (updates.valueType !== undefined)
      criterion.valueType = updates.valueType;
    if (updates.operator !== undefined) criterion.operator = updates.operator;
    if (updates.value !== undefined) criterion.value = updates.value;
    this.draftStore.revalidateTreeIfNeeded();
  }

  selectCriterionField(
    criterionPath: ZNodePath,
    field: ZFilterableField | null,
  ): void {
    this.updateCriterion(criterionPath, {
      fieldMeta: field?.fieldMeta ?? null,
      valueType: field?.type ?? null,
      operator: null,
      value: null,
    });
  }

  selectCriterionOperator(
    criterionPath: ZNodePath,
    operator: string | null,
  ): void {
    const criterion = this.findCriterionNode(criterionPath);
    if (!criterion) {
      return;
    }

    const shouldKeepValue =
      !!operator &&
      !!criterion.valueType &&
      this.doesOperatorRequireValue(operator, criterion.valueType);

    this.updateCriterion(criterionPath, {
      operator,
      value: shouldKeepValue ? criterion.value : null,
    });
  }

  setCriterionValue(criterionPath: ZNodePath, value: ZFilterValue): void {
    this.updateCriterion(criterionPath, { value });
  }

  private findCriterionNode(criterionPath: ZNodePath): ZCriterionNode | null {
    const criterion = findNodeByPath(this.rootGroup, criterionPath);

    if (!criterion || isGroupNode(criterion)) {
      return null;
    }

    return criterion;
  }

  /** Action: переместить узел вверх в пределах группы */
  moveNodeUp(nodePath: ZNodePath): void {
    moveNodeByDirection(this.rootGroup, nodePath, -1);
    this.pruneEmptyGroups();
    this.draftStore.revalidateTreeIfNeeded();
  }

  /** Action: переместить узел вниз в пределах группы */
  moveNodeDown(nodePath: ZNodePath): void {
    moveNodeByDirection(this.rootGroup, nodePath, 1);
    this.pruneEmptyGroups();
    this.draftStore.revalidateTreeIfNeeded();
  }

  get criteriaCount(): number {
    return countCriteria(this.rootGroup);
  }

  /**
   * Computed: проверка возможности перемещения узла вверх
   * @param nodePath - путь к узлу
   */
  canMoveUp(nodePath: ZNodePath): boolean {
    return canMoveNodeByDirection(this.rootGroup, nodePath, -1);
  }

  /**
   * Computed: проверка возможности перемещения узла вниз
   * @param nodePath - путь к узлу
   */
  canMoveDown(nodePath: ZNodePath): boolean {
    return canMoveNodeByDirection(this.rootGroup, nodePath, 1);
  }

  getChildrenMoveCapabilities(path: ZNodePath): {
    canMoveUp: boolean;
    canMoveDown: boolean;
  }[] {
    return getChildrenMoveCapabilities(this.rootGroup, path);
  }

  /**
   * Сериализация дерева в API структуру
   * @returns FilterGroup для отправки на бэкенд
   */
  toFilterGroup(
    context: ZValidationContext = validationContexts.apply,
  ): ZFilterGroup {
    return serializeFilterTree(
      this.rootGroup,
      this.currentOperatorsMapping,
      context,
    );
  }

  /**
   * Загрузка фильтра из API структуры
   * @param filterGroup - FilterGroup от бэкенда
   */
  loadFromFilterGroup(filterGroup: ZFilterGroup): void {
    this.setRootGroup(deserializeFilterTree(filterGroup));
    this.draftStore.reset();
  }
}
